import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Truck, MapPin, Package, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShippingLabelModal — Seller creates a shipping label via envia.com
 */
export function ShippingLabelModal({ open, onOpenChange, order, onDone }) {
    const [carriers, setCarriers] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [step, setStep] = useState(1); // 1: quote, 2: confirm
    const [loading, setLoading] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [origin, setOrigin] = useState({ postal_code: '', country: 'MX', state: '', city: '', address: '' });
    const [destination, setDestination] = useState({ postal_code: '', country: 'MX', state: '', city: '', address: '' });
    const [pkg, setPkg] = useState({ weight: 1, length: 20, width: 15, height: 10, content: 'Producto Ok te lo Vendo' });

    useEffect(() => {
        if (!open || !order) return;
        api.get('/shipping/carriers').then(r => setCarriers(r.data.carriers || []));
        setQuotes([]); setSelectedQuote(null); setStep(1);
    }, [open, order]);

    const doQuote = async () => {
        if (!origin.postal_code || !destination.postal_code) { toast.error('Ingresa códigos postales'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/shipping/quote', { origin, destination, packages: [pkg] });
            setQuotes(data.quotes || []);
            setStep(2);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setLoading(false); }
    };

    const createLabel = async () => {
        if (!selectedQuote) { toast.error('Selecciona una cotización'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/shipping/label', {
                order_id: order.id,
                carrier: selectedQuote.carrier,
                service: selectedQuote.service,
                origin, destination, packages: [pkg],
            });
            toast.success(`Etiqueta creada: ${data.tracking_number}`);
            onDone && onDone(data);
            onOpenChange(false);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setLoading(false); }
    };

    if (!order) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="shipping-label-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-red-600" />Crear guía de envío</DialogTitle>
                    <DialogDescription>Producto: {order.ad_snapshot?.title}</DialogDescription>
                </DialogHeader>
                {step === 1 ? (
                    <div className="space-y-3">
                        <div className="rounded-xl border p-3 space-y-2">
                            <div className="flex items-center gap-1 text-sm font-semibold"><MapPin className="h-4 w-4 text-red-600" />Origen (tú)</div>
                            <div className="grid grid-cols-2 gap-2">
                                <Input placeholder="Código postal" value={origin.postal_code} onChange={(e) => setOrigin({ ...origin, postal_code: e.target.value })} data-testid="ship-origin-zip" />
                                <Input placeholder="Ciudad" value={origin.city} onChange={(e) => setOrigin({ ...origin, city: e.target.value })} />
                                <Input placeholder="Estado" value={origin.state} onChange={(e) => setOrigin({ ...origin, state: e.target.value })} />
                                <Input placeholder="País" value={origin.country} onChange={(e) => setOrigin({ ...origin, country: e.target.value })} />
                                <Input placeholder="Dirección" className="col-span-2" value={origin.address} onChange={(e) => setOrigin({ ...origin, address: e.target.value })} />
                            </div>
                        </div>
                        <div className="rounded-xl border p-3 space-y-2">
                            <div className="flex items-center gap-1 text-sm font-semibold"><MapPin className="h-4 w-4 text-red-600" />Destino (comprador)</div>
                            <div className="grid grid-cols-2 gap-2">
                                <Input placeholder="Código postal" value={destination.postal_code} onChange={(e) => setDestination({ ...destination, postal_code: e.target.value })} data-testid="ship-dest-zip" />
                                <Input placeholder="Ciudad" value={destination.city} onChange={(e) => setDestination({ ...destination, city: e.target.value })} />
                                <Input placeholder="Estado" value={destination.state} onChange={(e) => setDestination({ ...destination, state: e.target.value })} />
                                <Input placeholder="País" value={destination.country} onChange={(e) => setDestination({ ...destination, country: e.target.value })} />
                                <Input placeholder="Dirección" className="col-span-2" value={destination.address} onChange={(e) => setDestination({ ...destination, address: e.target.value })} />
                            </div>
                        </div>
                        <div className="rounded-xl border p-3 space-y-2">
                            <div className="flex items-center gap-1 text-sm font-semibold"><Package className="h-4 w-4 text-red-600" />Paquete</div>
                            <div className="grid grid-cols-4 gap-2">
                                <div><Label className="text-[10px]">Peso (kg)</Label><Input type="number" step="0.1" value={pkg.weight} onChange={(e) => setPkg({ ...pkg, weight: Number(e.target.value) })} /></div>
                                <div><Label className="text-[10px]">Largo (cm)</Label><Input type="number" value={pkg.length} onChange={(e) => setPkg({ ...pkg, length: Number(e.target.value) })} /></div>
                                <div><Label className="text-[10px]">Ancho (cm)</Label><Input type="number" value={pkg.width} onChange={(e) => setPkg({ ...pkg, width: Number(e.target.value) })} /></div>
                                <div><Label className="text-[10px]">Alto (cm)</Label><Input type="number" value={pkg.height} onChange={(e) => setPkg({ ...pkg, height: Number(e.target.value) })} /></div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="ship-quotes-list">
                        {quotes.length === 0 && <div className="text-sm text-muted-foreground">Sin cotizaciones.</div>}
                        {quotes.map((q, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setSelectedQuote(q)}
                                className={`w-full flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all ${selectedQuote === q ? 'border-red-600 bg-red-50' : 'border-neutral-200 hover:border-red-400'}`}
                                data-testid={`quote-${i}`}
                            >
                                <Truck className="h-5 w-5 text-red-600" />
                                <div className="flex-1">
                                    <div className="font-semibold text-sm capitalize">{q.carrier} · <span className="text-neutral-500">{q.service}</span></div>
                                    <div className="text-xs text-muted-foreground">Entrega estimada: {q.days || '—'} días</div>
                                </div>
                                <div className="font-bold text-red-600">${Number(q.amount).toFixed(2)} {q.currency || 'MXN'}</div>
                            </button>
                        ))}
                    </div>
                )}
                <DialogFooter>
                    {step === 2 && <Button variant="outline" onClick={() => setStep(1)}>Atrás</Button>}
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    {step === 1 ? (
                        <Button onClick={doQuote} disabled={loading} className="bg-red-600 hover:bg-red-700" data-testid="ship-get-quotes">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cotizar envío'}
                        </Button>
                    ) : (
                        <Button onClick={createLabel} disabled={loading || !selectedQuote} className="bg-red-600 hover:bg-red-700" data-testid="ship-create-label">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear guía'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/**
 * TrackingModal — Buyer sees the tracking timeline
 */
export function TrackingModal({ open, onOpenChange, order }) {
    const [track, setTrack] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !order?.shipping?.tracking_number) return;
        setLoading(true);
        api.get(`/shipping/track/${order.shipping.tracking_number}`, { params: { carrier: order.shipping.carrier } })
            .then(r => setTrack(r.data))
            .catch(() => setTrack(null))
            .finally(() => setLoading(false));
    }, [open, order]);

    if (!order) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md" data-testid="tracking-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-red-600" />Seguimiento</DialogTitle>
                    <DialogDescription>Producto: {order.ad_snapshot?.title}</DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="py-4 text-center text-sm"><Loader2 className="h-5 w-5 mx-auto animate-spin text-red-600" /></div>
                ) : !order.shipping?.tracking_number ? (
                    <div className="text-sm text-muted-foreground py-4 text-center">Aún no hay guía de envío.</div>
                ) : (
                    <div className="space-y-3">
                        <div className="rounded-xl border p-3 flex items-center justify-between">
                            <div>
                                <div className="text-xs text-muted-foreground">Paquetería</div>
                                <div className="font-semibold capitalize">{order.shipping.carrier}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground text-right">Tracking</div>
                                <div className="font-mono text-sm">{order.shipping.tracking_number}</div>
                            </div>
                            {order.shipping.label_url && (
                                <a href={order.shipping.label_url} target="_blank" rel="noreferrer" className="text-red-600 hover:underline text-xs" data-testid="tracking-label-link">Ver etiqueta<ExternalLink className="h-3 w-3 inline ml-1" /></a>
                            )}
                        </div>
                        {track ? (
                            <div>
                                <Badge variant="outline" className="capitalize">{track.status || 'estado desconocido'}</Badge>
                                <ol className="mt-3 space-y-2 border-l-2 border-red-200 pl-3">
                                    {(track.events || []).map((ev, i) => (
                                        <li key={i} className="relative">
                                            <div className="absolute -left-[10px] top-1 h-3 w-3 rounded-full bg-red-600" />
                                            <div className="text-sm">{ev.description}</div>
                                            <div className="text-[11px] text-muted-foreground">{ev.location} · {new Date(ev.date).toLocaleString('es-MX')}</div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        ) : (
                            <div className="text-sm text-muted-foreground">Sin actualizaciones aún.</div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
